'use client'
import { CloseIcon } from '../../../icons'
import React, { useId } from 'react'
import { NotificationProps, NotificationVariant } from '../../../../types/Notification'
import { Button, ButtonVariants, Text } from '../../../atoms'
import { getNotificationIcon } from '../helpers/getNotificationIcon'

import { renderMessage } from '../helpers/renderMessage'

export interface FixedNotificationProps extends NotificationProps {
    actions?: React.ReactNode[]
    dismissLabel: string
}

export const FixedNotification = ({
    id,
    title,
    message,
    variant = NotificationVariant.INFO,
    dismissable = true,
    hideIcon = false,
    customIcon,
    actions,
    dismissLabel,
    onRemove,
    ...props
}: FixedNotificationProps) => {
    const notificationId = id ?? useId()

    const typeClasses = {
        [NotificationVariant.SHADE]: 'bg-shade-opacity-100 text-shade-700',
        [NotificationVariant.INFO]: 'bg-info-100 text-info-700',
        [NotificationVariant.SUCCESS]: 'bg-success-100 text-success-700',
        [NotificationVariant.WARNING]: 'bg-warning-100 text-warning-700',
        [NotificationVariant.ERROR]: 'bg-error-100 text-error-700',
    }

    const iconElement = getNotificationIcon(variant, customIcon)

    return (
        <dialog
            data-testid="fixed-notification"
            className={`fixed-notification border border-shade-opacity-200 shadow-drop-md relative w-96 flex p-0 ${typeClasses[variant]}`}
            {...props}
            open
        >
            <div className="py-6 px-4 flex items-start min-w-0">
                {!hideIcon && iconElement && <div className="text-current shrink-0">{iconElement}</div>}
                <div className="ml-2 min-w-0">
                    {title && <Text className="font-medium text-text-primary">{title}</Text>}
                    {renderMessage({ message, classes: 'text-text-secondary' })}
                </div>
            </div>
            {dismissable && (!actions || actions.length === 0) && (
                <div className="ml-auto mr-2 my-2 shrink-0 inline-flex items-center">
                    <Button
                        data-testid="close-button"
                        variant={ButtonVariants.QUIET}
                        icon={<CloseIcon />}
                        onClick={() => onRemove && onRemove(notificationId)}
                    />
                </div>
            )}
            {actions && actions.length > 0 && (
                <div className="fixed-notification__actions ml-auto inline-flex flex-col border-l border-l-shade-opacity-200 shrink-0">
                    {actions.map((action, index) => {
                        return (
                            <div
                                className="first:border-b border-b-shade-opacity-200 inline-flex flex-grow"
                                key={index}
                            >
                                {action}
                            </div>
                        )
                    })}
                    {actions.length <= 1 && dismissable && dismissLabel && (
                        <div className="first:border-b border-b-shade-opacity-200 inline-flex flex-grow">
                            <Button variant={ButtonVariants.QUIET} onClick={() => onRemove && onRemove(notificationId)}>
                                {dismissLabel}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </dialog>
    )
}
